import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import type { RunDetail } from '@/entities/sender.entity';
import { useState } from 'react';

export type SenderRunAdminAction = 'cancel' | 'resume';

type SenderRunActionConfirmDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	action: SenderRunAdminAction | null;
	run: RunDetail | null;
	onConfirm: () => Promise<void>;
	loading?: boolean;
};

const ACTION_COPY: Record<
	SenderRunAdminAction,
	{ title: string; step1: string; step2: string; confirmLabel: string; destructive?: boolean }
> = {
	cancel: {
		title: 'Отменить запуск',
		step1: 'Запуск будет остановлен. Уже запланированные сообщения в Telegram отозвать нельзя.',
		step2: 'Подтвердите отмену запуска для пользователя. Действие необратимо.',
		confirmLabel: 'Да, отменить',
		destructive: true,
	},
	resume: {
		title: 'Возобновить рассылку',
		step1: 'Запуск снова попадёт в очередь dispatch и продолжится с checkpoint.',
		step2: 'Подтвердите возобновление рассылки от имени пользователя.',
		confirmLabel: 'Да, возобновить',
	},
};

export default function SenderRunActionConfirmDialog({
	open,
	onOpenChange,
	action,
	run,
	onConfirm,
	loading = false,
}: SenderRunActionConfirmDialogProps) {
	const [step, setStep] = useState<1 | 2>(1);

	const handleOpenChange = (nextOpen: boolean) => {
		if (!nextOpen) {
			setStep(1);
		}
		onOpenChange(nextOpen);
	};

	if (!action || !run) {
		return null;
	}

	const copy = ACTION_COPY[action];

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className='sm:max-w-[440px]'>
				<DialogHeader>
					<DialogTitle>{copy.title}</DialogTitle>
					<DialogDescription className='break-words'>
						{step === 1 ? copy.step1 : copy.step2}
					</DialogDescription>
				</DialogHeader>

				<div className='rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm space-y-1'>
					<p className='font-medium truncate'>{run.caption}</p>
					<p className='text-xs text-muted-foreground font-mono break-all'>{run.id}</p>
				</div>

				<DialogFooter className='gap-2 sm:gap-0'>
					{step === 1 ? (
						<>
							<Button type='button' variant='outline' onClick={() => handleOpenChange(false)}>
								Закрыть
							</Button>
							<Button type='button' onClick={() => setStep(2)}>
								Продолжить
							</Button>
						</>
					) : (
						<>
							<Button type='button' variant='outline' onClick={() => setStep(1)} disabled={loading}>
								Назад
							</Button>
							<Button
								type='button'
								variant={copy.destructive ? 'destructive' : 'default'}
								disabled={loading}
								onClick={() => void onConfirm()}
							>
								{copy.confirmLabel}
							</Button>
						</>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
